import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Mail, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSubmitted(true);
    } catch (err) {
      // Endpoint always returns 200 — anything else is a server error.
      toast.error("Could not process request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="mb-6">
          <Link to="/login" data-testid="back-to-login"
            className="inline-flex items-center gap-1 text-sm text-ink-600 hover:text-brand font-semibold">
            <ArrowLeft size={14} /> Back to sign in
          </Link>
        </div>

        <div className="bg-white rounded-3xl shadow-card p-8 border border-border">
          {!submitted ? (
            <>
              <div className="flex items-center gap-3 mb-1">
                <div className="p-2 rounded-full bg-brand/10 text-brand"><Mail size={20} /></div>
                <h1 className="font-heading text-2xl font-extrabold text-ink">Forgot your password?</h1>
              </div>
              <p className="text-sm text-ink-600 mt-2">
                Enter the email tied to your Grubpe account and we&apos;ll send a reset link.
              </p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Email</label>
                  <input data-testid="forgot-email" type="email" required autoFocus
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-border bg-white px-4 py-3 text-ink placeholder:text-ink-400 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                    placeholder="you@school.edu" />
                </div>
                <button data-testid="forgot-submit" disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-brand text-white font-semibold hover:bg-brand-600 active:scale-95 disabled:opacity-60">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
                  Send reset link
                </button>
              </form>
              <div className="mt-4 text-[11px] text-ink-500 leading-relaxed">
                Note: We&apos;ll always return success — if no Grubpe account exists for this email, you simply won&apos;t receive anything.
              </div>
            </>
          ) : (
            <div className="text-center" data-testid="forgot-success">
              <CheckCircle2 size={48} className="mx-auto text-emerald-500 mb-3" />
              <h2 className="font-heading text-2xl font-extrabold text-ink">Check your inbox</h2>
              <p className="text-sm text-ink-600 mt-2">
                If <span className="font-semibold text-ink">{email}</span> matches a Grubpe account, a password
                reset link is on its way. The link expires in 60 minutes.
              </p>
              <Link to="/login" className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-brand hover:underline">
                <ArrowLeft size={14} /> Back to sign in
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
